import {Action, Selector, State, StateContext} from '@ngxs/store';
import {GeneralContract} from '../../models/api/product/base.model';
import {Injectable} from '@angular/core';
import {PolicyMassImportActions} from './policy-mass-import.actions';
import {catchError, tap} from 'rxjs/operators';
import {HttpErrorResponse} from '@angular/common/http';
import {getTranslatedErrors} from '../../helpers/validation.helper';
import {of} from 'rxjs';
import {GeneralContractService} from '../../services/general-contract.service';
import {NotificationService} from '../../services/notification.service';
import {TranslateService} from '@ngx-translate/core';
import {FileService} from '../../services/file.service';
import {FleetImportProgress} from '../../models/api/fleet/fleet-import-progress.model';
import {PolicyService} from '../../services/policy.service';
import {NavService} from '../../services/nav.service';
import {NavItemCodes} from '../../enums/nav-item-codes.enum';

interface PolicyMassImportStateModel {
    generalContract?: GeneralContract;
    massPolicyImportErrors?: string[];
    importProgress?: FleetImportProgress;
    importKillRequested?: boolean;
}

@State<PolicyMassImportStateModel>({
    name: PolicyMassImportState.STATE_NAME,
    defaults: {
        generalContract: null,
        massPolicyImportErrors: null,
        importProgress: null,
        importKillRequested: null,
    },
})
@Injectable()
export class PolicyMassImportState {
    static STATE_NAME = 'policyMassImport';

    constructor(
        protected generalContractService: GeneralContractService,
        protected notificationService: NotificationService,
        protected translateService: TranslateService,
        protected fileService: FileService,
        protected policyService: PolicyService,
        protected navService: NavService
    ) {}

    @Selector()
    static generalContract(state: PolicyMassImportStateModel) {
        return state.generalContract;
    }

    @Selector()
    static massImportErrors(state: PolicyMassImportStateModel) {
        return state.massPolicyImportErrors;
    }

    @Selector()
    static importProgress(state: PolicyMassImportStateModel) {
        return state.importProgress;
    }

    @Selector()
    static importKillRequested(state: PolicyMassImportStateModel) {
        return state.importKillRequested;
    }

    @Action(PolicyMassImportActions.Init)
    init(ctx: StateContext<PolicyMassImportStateModel>, {generalContractId}: PolicyMassImportActions.Init) {
        return this.generalContractService.getById(generalContractId).pipe(
            tap((generalContract) =>
                ctx.patchState({
                    generalContract,
                })
            )
        );
    }

    @Action(PolicyMassImportActions.Import)
    importMassPolicies(
        ctx: StateContext<PolicyMassImportStateModel>,
        {file, generalContractId}: PolicyMassImportActions.Import
    ) {
        return this.generalContractService.massRegisterPolicies(generalContractId, file).pipe(
            tap(() => {
                this.notificationService.success(this.translateService.instant('MASS_REGISTER.MASS_REGISTER_SUCCESS'));
                ctx.patchState({massPolicyImportErrors: null});
            }),
            catchError((error: HttpErrorResponse) => {
                if (error.status === 422) {
                    ctx.patchState({
                        massPolicyImportErrors: getTranslatedErrors(
                            error?.error?.entities,
                            3,
                            this.translateService.instant('LABELS.ROW')
                        ),
                    });
                }
                return of(error);
            })
        );
    }

    @Action(PolicyMassImportActions.GetTemplateFile)
    getMassRegisterTemplateFile(_, {assetName}: PolicyMassImportActions.GetTemplateFile) {
        this.fileService.getAssetByName(assetName);
    }

    @Action(PolicyMassImportActions.Clear)
    clear(ctx: StateContext<PolicyMassImportStateModel>) {
        ctx.patchState({
            generalContract: null,
            massPolicyImportErrors: null,
        });
    }

    @Action(PolicyMassImportActions.ImportFleet)
    importFleet(
        ctx: StateContext<PolicyMassImportStateModel>,
        {file, generalContractId, type}: PolicyMassImportActions.ImportFleet
    ) {
        return this.generalContractService.massRegisterFleetPolicies(generalContractId, file, type).pipe(
            tap((response: number) => {
                this.notificationService.success(this.translateService.instant('MASS_REGISTER.MASS_REGISTER_QUEUED'));
                // navigate to import progress page
                this.navService.navigateByCode(NavItemCodes.POLICY_IMPORT_PROGRESS, response);
            }),
            catchError((error: HttpErrorResponse) => {
                if (error.status === 422) {
                    ctx.patchState({
                        massPolicyImportErrors: getTranslatedErrors(
                            error?.error?.entities,
                            3,
                            this.translateService.instant('LABELS.ROW')
                        ),
                    });
                }
                return of(error);
            })
        );
    }

    @Action(PolicyMassImportActions.FetchImportProgress)
    fetchImportProgress(
        ctx: StateContext<PolicyMassImportStateModel>,
        {importId, httpOptions}: PolicyMassImportActions.FetchImportProgress
    ) {
        return this.policyService.fleetImportProgress(importId, httpOptions).pipe(
            tap((importProgress) => {
                importProgress.importId = importId;
                ctx.patchState({importProgress});
            })
        );
    }

    @Action(PolicyMassImportActions.ClearImportProgressData)
    clearFleetProgressResult(ctx: StateContext<PolicyMassImportStateModel>) {
        ctx.patchState({
            importProgress: null,
            importKillRequested: null,
        });
    }

    @Action(PolicyMassImportActions.ImportKill)
    importKill(ctx: StateContext<PolicyMassImportStateModel>, {importId}: PolicyMassImportActions.ImportKill) {
        ctx.patchState({
            importKillRequested: true,
        });
        this.notificationService.success(this.translateService.instant('MASS_REGISTER.IMPORT_KILL_SCHEDULED'));
        return this.policyService.importKill(importId);
    }
}
